import { addDays, differenceInCalendarDays, format, parseISO } from "date-fns";
import type { Database, NFPMethod } from "@nfp-tracker/utils";
import { computeFirstPeakDay, determinePostPeakProgress } from "@nfp-tracker/utils";

export type ObservationRow = Database["public"]["Tables"]["observations"]["Row"];

export type DerivedCycle = {
  id: string;
  /**
   * First day of the cycle. For a partial cycle (`start_inferred`) this is only the earliest
   * day logged, not the real CD 1 — the actual start was never recorded.
   */
  start_date: string;
  end_date: string | null;
  first_peak_cd: number | null;
  predicted_avoid_start_cd: number | null;
  post_peak_progress: string | null;
  /**
   * True for the leading span of observations logged before the first day marked "cycle
   * start". Its real CD 1 is unknown, so it's a partial cycle: its day numbers are only
   * offsets from the first logged day, it must not be labelled as a cycle start, and it's
   * excluded from cycle-length statistics and predictions.
   */
  start_inferred: boolean;
  /** Latest observation date in the cycle (may be in the future for pre-logged entries). */
  last_observation_date: string;
};

export type DerivedObservation = ObservationRow & {
  date: string;
  /**
   * Day number within the cycle. For an observation in a partial cycle
   * (`cycle_start_inferred`) this is counted from the first logged day rather than the real
   * CD 1, so it's only meaningful relative to other days in the same span — keep it for
   * ordering and Peak detection, don't display it as a cycle day.
   */
  cycle_day: number;
  cycle_id: string;
  cycle_start_inferred: boolean;
};

export {
  getCurrentCycle,
  getCycleTiming,
  isPastCycle,
  isUpcomingCycle,
  type CycleTiming,
} from "@nfp-tracker/utils";

const cycleIdFromStart = (startDate: string) => `cycle-${startDate}`;

/** Cycles whose start was actually recorded — the only ones lengths/predictions may use. */
export const isRecordedCycle = <T extends { start_inferred?: boolean | null }>(cycle: T) =>
  !cycle.start_inferred;

export const deriveCyclesFromObservations = (
  source: ObservationRow[],
  methods: string[] = ["Marquette"]
): { cycles: DerivedCycle[]; observations: DerivedObservation[] } => {
  if (!source.length) {
    return { cycles: [], observations: [] };
  }

  const sorted = [...source].sort((a, b) =>
    a.observation_date.localeCompare(b.observation_date)
  );

  // Only a day explicitly marked "cycle start" begins a cycle (the same rule the
  // send-fertility-alert function uses server-side). Days logged before the first marked
  // start still need a home, so they form one leading partial cycle whose real start is
  // unknown — previously that span was silently presented as starting on its first logged
  // day, which turned e.g. a Peak reading entered as someone's first observation into "CD 1".
  const cycleStarts: { date: string; inferred: boolean }[] = [];
  for (const obs of sorted) {
    const last = cycleStarts[cycleStarts.length - 1];
    if (cycleStarts.length === 0) {
      cycleStarts.push({ date: obs.observation_date, inferred: !obs.is_cycle_start });
    } else if (obs.is_cycle_start && last.date !== obs.observation_date) {
      cycleStarts.push({ date: obs.observation_date, inferred: false });
    } else if (obs.is_cycle_start && last.date === obs.observation_date) {
      // Duplicate rows for the first day, where a later one carries the flag.
      last.inferred = false;
    }
  }

  const cyclesAsc: DerivedCycle[] = cycleStarts.map(({ date: startDate, inferred }, idx) => {
    const nextStart = cycleStarts[idx + 1]?.date;
    const endDate = nextStart
      ? format(addDays(parseISO(nextStart), -1), "yyyy-MM-dd")
      : null;

    return {
      id: cycleIdFromStart(startDate),
      start_date: startDate,
      end_date: endDate,
      first_peak_cd: null,
      predicted_avoid_start_cd: null,
      post_peak_progress: null,
      start_inferred: inferred,
      last_observation_date: startDate,
    };
  });

  const observations: DerivedObservation[] = sorted.map((obs) => {
    const cycle =
      [...cyclesAsc]
        .reverse()
        .find((c) => c.start_date <= obs.observation_date) || cyclesAsc[0];

    const cycleDay =
      differenceInCalendarDays(
        parseISO(obs.observation_date),
        parseISO(cycle.start_date)
      ) + 1;

    return {
      ...obs,
      date: obs.observation_date,
      cycle_day: Math.max(1, cycleDay),
      cycle_id: cycle.id,
      cycle_start_inferred: cycle.start_inferred,
    };
  });

  const methodList = methods as NFPMethod[];
  const enrichedCycles: DerivedCycle[] = cyclesAsc.map((cycle) => {
    const cycleObs = observations.filter((obs) => obs.cycle_id === cycle.id);
    // Peak detection still runs on a partial cycle — it only depends on the order of days,
    // so the Peak is found on the right date; its CD is just relative to the first logged day.
    const firstPeakCd = computeFirstPeakDay(cycleObs, methodList);
    return {
      ...cycle,
      last_observation_date: cycleObs[cycleObs.length - 1]?.date ?? cycle.start_date,
      first_peak_cd: firstPeakCd,
      post_peak_progress: firstPeakCd
        ? determinePostPeakProgress(cycleObs, firstPeakCd, methodList)
        : null,
    };
  });

  return {
    cycles: enrichedCycles.reverse(),
    observations,
  };
};

/**
 * The cycle a date falls in. An open-ended (latest) cycle runs through today, or through its
 * last logged day when entries were pre-logged past today — so a future-dated entry resolves
 * to the same cycle deriveCyclesFromObservations assigned it to.
 */
export const getCycleForDate = (
  cycles: DerivedCycle[],
  dateKey: string,
  todayKey: string
): DerivedCycle | null => {
  return (
    cycles.find((cycle) => {
      const openEnd =
        cycle.last_observation_date && cycle.last_observation_date > todayKey
          ? cycle.last_observation_date
          : todayKey;
      const end = cycle.end_date || openEnd;
      return dateKey >= cycle.start_date && dateKey <= end;
    }) || null
  );
};
